import React, { useState } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { loginWithPasswordApi, requestOtpApi, loginWithOtpApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PasswordLoginForm from '../components/auth/PasswordLoginForm';
import OtpLoginForm from '../components/auth/OtpLoginForm';
import SocialLoginButtons from '../components/auth/SocialLoginButtons';
import './LoginPage.css';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();

    // Form States
    const [loginMethod, setLoginMethod] = useState('password');
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // UI States
    const [otpSent, setOtpSent] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(false);

    const handleTabSelect = (k) => {
        setLoginMethod(k);
        setOtpSent(false);
        setMessage({ type: '', text: '' });
    };

    const extractToken = (resPayload, rawRes) => {
        if (!resPayload) return null;
        if (typeof resPayload === 'string' && resPayload.length > 20) return resPayload;
        
        if (resPayload.token && typeof resPayload.token === 'string') return resPayload.token;
        if (resPayload.data?.token && typeof resPayload.data.token === 'string') return resPayload.data.token;
        if (resPayload.data?.data?.token && typeof resPayload.data.data.token === 'string') return resPayload.data.data.token;
        if (resPayload.jwt && typeof resPayload.jwt === 'string') return resPayload.jwt;
        if (resPayload.accessToken && typeof resPayload.accessToken === 'string') return resPayload.accessToken;
        if (resPayload.access_token && typeof resPayload.access_token === 'string') return resPayload.access_token;

        if (rawRes?.data?.token && typeof rawRes.data.token === 'string') return rawRes.data.token;
        if (rawRes?.data?.data?.token && typeof rawRes.data.data.token === 'string') return rawRes.data.data.token;

        return null;
    };

    const handleSuccessfulLogin = (apiResponseData, rawResponse) => {
        const actualToken = extractToken(apiResponseData, rawResponse);
        
        if (!actualToken) {
            console.error("No valid token found in login response!", apiResponseData, rawResponse);
            setMessage({ type: 'danger', text: 'Login succeeded but server authorization token was missing.' });
            return;
        }

        const dataObj = apiResponseData?.data && typeof apiResponseData.data === 'object' ? apiResponseData.data : apiResponseData;

        const userPayload = {
            id: dataObj.id || dataObj.userId || apiResponseData.id,
            fullName: dataObj.fullName || dataObj.full_name || apiResponseData.fullName,
            email: dataObj.email || apiResponseData.email,
            role: dataObj.role || apiResponseData.role,
            roleCode: dataObj.roleCode || dataObj.role_code || apiResponseData.roleCode,
            joget_user_id: dataObj.joget_user_id || apiResponseData.joget_user_id,
            parentUserId: dataObj.parentUserId || apiResponseData.parentUserId || null,
            permissions: dataObj.permissions || apiResponseData.permissions || [] 
        };

        login(actualToken, userPayload);
        navigate('/dashboard');
    };

    const handleError = (err) => {
        const errorMsg = err.response?.data?.message || 'Something went wrong. Please try again.';
        setMessage({ type: 'danger', text: errorMsg });
    };

    const handlePasswordLogin = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setLoading(true);
        try {
            const res = await loginWithPasswordApi({ email: identifier, password });
            const dataPayload = res?.data || res;
            handleSuccessfulLogin(dataPayload, res);
        } catch (err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        setLoading(true);
        try {
            const { data } = await requestOtpApi({ identifier });
            setOtpSent(true);
            setMessage({ type: 'success', text: data.message || 'OTP sent successfully!' });
        } catch (err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleOtpLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await loginWithOtpApi({ identifier, otp });
            const dataPayload = res?.data || res;
            handleSuccessfulLogin(dataPayload, res);
        } catch (err) {
            setMessage({ type: 'danger', text: 'Invalid or expired OTP.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            pageTitle="Login"
            sidebarTitle="Welcome Back"
            subtitle="Sign in to your account to continue"
            className="login-auth-layout" 
        >
            <Tabs
                activeKey={loginMethod}
                onSelect={handleTabSelect}
                className="mb-4 custom-login-tabs"
                justify
            >
                <Tab eventKey="password" title="Password">
                    <PasswordLoginForm
                        identifier={identifier}
                        setIdentifier={setIdentifier}
                        password={password}
                        setPassword={setPassword}
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                        handlePasswordLogin={handlePasswordLogin}
                        message={message}
                        loading={loading}
                    />
                </Tab>

                <Tab eventKey="otp" title="Login with OTP">
                    <OtpLoginForm
                        identifier={identifier}
                        setIdentifier={setIdentifier}
                        otp={otp}
                        setOtp={setOtp}
                        otpSent={otpSent}
                        setOtpSent={setOtpSent}
                        handleRequestOtp={handleRequestOtp}
                        handleOtpLogin={handleOtpLogin}
                        message={message}
                        loading={loading}
                    />
                </Tab>
            </Tabs>

            <SocialLoginButtons />

            <div className="text-center mt-4">
                <p className="small text-secondary">
                    Don't have an account? <Link to="/register" className="signup-link">Sign up</Link>
                </p>
            </div>
        </AuthLayout>
    );
};

export default LoginPage;